import * as loginApi from '@/api/login';
import * as userApi from '@/api/user';
import type { UserInfo } from '@/api/login';

export const useUserStore = defineStore('userStore', () => {
  const userInfo = ref<UserInfo>({
    username: getUserName(),
    password: '',
  });

  const token = ref(getToken());
  const roles = ref<string[] | null>(null);
  const permissions = ref<string[]>([]);
  const userList = ref<Record<string, any>[]>([]);
  const clientManagerList = ref<Record<string, any>[]>([]);

  const userId = computed(() => userInfo.value?.userId || '');
  const getUserLabel = computed(() => {
    return (id: string) => userList.value.find((item: any) => item.userId.toString() === id)?.nickName || '-';
  });

  const clearToken = () => {
    token.value = '';
    removeToken();
  };

  const clearInfo = () => {
    clearToken();
    userInfo.value = { username: '', password: '' };
    roles.value = null;
    permissions.value = [];
    useAppStore().resetApp();
    useDictStore().resetDict();
    useRouterStore().resetRouter();
    useTableStore().resetTableField();
  };

  const login = async (info: UserInfo) => {
    try {
      info.username = (String(info.username) || '').trim();
      const data = await loginApi.login(info);
      token.value = data.token;
      setToken(data.token);
      return data;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const unLockLogin = async (info: UserInfo) => {
    try {
      info.username = (String(info.username) || '').trim();
      const data = await loginApi.unlock(info);
      token.value = data.token;
      setToken(data.token);
      return data;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const tokenlogin = async (triToken: string) => {
    try {
      const data = await loginApi.tokenLogin(triToken);
      token.value = data.token;
      setToken(data.token);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const getInfo = async () => {
    try {
      const res: any = await loginApi.getInfo();
      const { user } = res;
      user.avatar = user.avatar
        ? `${useAppStore().ROSA_BASE_API}${user.avatar}`
        : new URL('@/assets/images/avatar.png', import.meta.url).href;
      userInfo.value = user;
      roles.value = res.roles;
      permissions.value = res.permissions;
      return res;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const logout = async () => {
    try {
      if (import.meta.env.ROSA_IS_SSO !== 'TRUE') {
        clearInfo();
        await loginApi.logout();
      } else {
        // cas配置
        clearInfo();
        location.replace(import.meta.env.ROSA_SSO_URL);
      }
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const getUserList = async (params?: Record<string, any>) => {
    try {
      if (userList.value?.length) return;
      const { data } = await userApi.getUserList(params);
      userList.value =
        data &&
        data.map((item: any) => {
          return {
            ...item,
            userId: String(item.userId),
          };
        });
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const getClientManagerList = () => {
    userApi.getActiveClientManagerList().then((res: any) => {
      if (res.code === 200) {
        clientManagerList.value = res.data;
      } else {
        console.error('获取客户经理列表失败');
      }
    });
  };

  return {
    userInfo,
    token,
    roles,
    permissions,
    userList,
    userId,
    clientManagerList,
    getUserLabel,
    getClientManagerList,
    removeToken,
    clearInfo,
    login,
    tokenlogin,
    getInfo,
    logout,
    getUserList,
    unLockLogin,
  };
});
